import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { AsyncSection, Badge, SegmentedControl } from "../AgentAnalytics/ds";
import { useTableStyles } from "../AgentAnalytics/tableStyles";
import { fetchAnalytics } from "./adTemplateApi";
import { freeMeter, int, relativeDay } from "./formatters";
import { toLeadRow, verifiedVia } from "./publicLeadRow";
import PublicLeadDrawer from "./PublicLeadDrawer";

export const PUBLIC_LEAD_COLUMNS = [
  { key: "name", label: "Lead" },
  { key: "phone", label: "Phone" },
  { key: "verifiedVia", label: "Verified" },
  { key: "generationsUsed", label: "Generations", sortable: true },
  { key: "templateCount", label: "Prompts done", numeric: true },
  { key: "carousel", label: "Carousel" },
  { key: "signedUp", label: "Signed up" },
  { key: "createdAt", label: "First seen", sortable: true },
];

export const PUBLIC_LEAD_EXPORT_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "verifiedVia", label: "Verified" },
  { key: "generationsUsed", label: "Generations used" },
  { key: "templateCount", label: "Prompts done" },
  { key: "carousel", label: "Carousel" },
  { key: "signedUp", label: "Signed up project" },
  { key: "assistantId", label: "Project ID" },
  { key: "prompt", label: "Last prompt" },
  { key: "createdAt", label: "First seen" },
];

const STAGES = [
  { value: "all", label: "All" },
  { value: "verified", label: "Verified" },
  { value: "generated", label: "Generated" },
  { value: "carousel_sent", label: "Carousel sent" },
  { value: "signed_up", label: "Signed up" },
];

const useLocalStyles = makeStyles({
  panel: { padding: 16, display: "flex", flexDirection: "column", gap: 14 },
  centeredTable: {
    "& td, & th": { textAlign: "center" },
  },
  num: {
    textAlign: "center",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  badges: { display: "inline-flex", gap: 4, flexWrap: "wrap", justifyContent: "center" },
});

export default function PublicLeadsView({ range, search, refreshKey, onRows }) {
  const classes = { ...useTableStyles(), ...useLocalStyles() };
  const [state, setState] = useState({
    loading: true,
    notFound: false,
    items: [],
    total: 0,
    error: null,
  });
  const [stage, setStage] = useState("all");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("createdAt");
  const [dir, setDir] = useState("desc");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [range.from, range.to, search, stage]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fetchAnalytics("public-leads", {
      from: range.from,
      to: range.to,
      page: page + 1,
      sort,
      dir,
      search,
      stage: stage === "all" ? "" : stage,
    }).then((r) => {
      if (!alive) return;
      const items = ((r.data && r.data.items) || []).map(toLeadRow);
      setState({
        loading: false,
        notFound: r.notFound,
        items,
        total: (r.data && r.data.total) || 0,
        error: r.error,
      });
      if (onRows) onRows(items, PUBLIC_LEAD_EXPORT_COLUMNS);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, page, sort, dir, search, stage, refreshKey]);

  const toggleSort = (key) => {
    if (sort === key) {
      setDir(dir === "asc" ? "desc" : "asc");
      return;
    }
    setSort(key);
    setDir("desc");
  };

  return (
    <>
      <Box className={classes.panel}>
        <Box>
          <SegmentedControl
            value={stage}
            onChange={setStage}
            options={STAGES}
            data-testid="stage-filter"
          />
        </Box>
      </Box>

      <AsyncSection
        loading={state.loading}
        notFound={state.notFound}
        error={state.error}
        empty={!state.items.length}
        emptyTitle="No public page leads in this range"
        emptyDescription="Leads show up once someone fills the form on the public Ads Generator page."
      >
        <Box data-testid="public-leads-table">
          <div className={`${classes.tableWrap} ${classes.centeredTable}`}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {PUBLIC_LEAD_COLUMNS.map((c) => (
                    <TableCell key={c.key} align="center" className={classes.headCell}>
                      {c.sortable ? (
                        <TableSortLabel
                          className={classes.sortLabel}
                          active={sort === c.key}
                          direction={sort === c.key ? dir : "desc"}
                          onClick={() => toggleSort(c.key)}
                        >
                          {c.label}
                        </TableSortLabel>
                      ) : (
                        c.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.items.map((lead) => (
                  <TableRow
                    key={lead._id}
                    hover
                    className={classes.clickRow}
                    data-testid={`lead-row-${lead._id}`}
                    onClick={() => setSelected(lead)}
                  >
                    <TableCell className={classes.cell}>
                      <Typography component="div" className={classes.nameText}>
                        {lead.name || "—"}
                      </Typography>
                      <Typography component="div" className={classes.monoSub}>
                        {lead.email || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {lead.phone}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <span className={classes.badges}>
                        {verifiedVia(lead).length ? (
                          verifiedVia(lead).map((via) => (
                            <Badge key={via} tone="success" subtle>{via}</Badge>
                          ))
                        ) : (
                          <Badge tone="neutral" subtle>Not verified</Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {freeMeter(lead.generationsUsed || 0, lead.generationsLimit)}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {int(lead.templateCount)}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Badge tone={lead.waMessageSent ? "success" : "neutral"} subtle>
                        {lead.carousel}
                      </Badge>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {lead.claimedAt ? (
                        <>
                          <Typography component="div" className={classes.nameText}>
                            {lead.projectName || lead.assistantId}
                          </Typography>
                          <Typography component="div" className={classes.monoSub}>
                            {relativeDay(lead.claimedAt)}
                          </Typography>
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>{relativeDay(lead.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <TablePagination
            component="div"
            count={state.total}
            page={page}
            onChangePage={(event, next) => setPage(next)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        </Box>
      </AsyncSection>

      <PublicLeadDrawer lead={selected} onClose={() => setSelected(null)} />
    </>
  );
}
